/**
 * Wissen — seit dem Umbau „Drei Fragen statt drei Systembegriffe" (Antoines Go 05.08.2026) in
 * drei Reitern, die Fragen beantworten statt Systembegriffe zu nennen:
 *
 *  1. „Was MAESTRO weiß"   — Bausteine (knowledge_chunks) UND Beobachtetes (Fakten + Einsichten
 *     aus GET /api/gedaechtnis/beobachtet) in EINER Fläche, jeder Eintrag mit Herkunfts-Badge
 *     („Von Ihnen" / „Beobachtet" / „Aus Referenzen"). Oben EIN Eingabefeld „Wissen hinzufügen"
 *     (Freitext → POST /api/gedaechtnis/fakt, wird bestätigter Fakt — der Nutzer entscheidet nie
 *     einen Typ). Widerspruchsknöpfe und aufklappbare Belege bleiben exakt erhalten
 *     (beobachtet-tab.tsx). Die Baustein-Pflege (anlegen/bearbeiten/Sichtbarkeit) bleibt
 *     vollständig erreichbar — als Karten in derselben Liste plus Drawer.
 *  2. „Wie MAESTRO handelt" — die Skills/Playbooks (playbooks-tab.tsx), nur neu beschriftet.
 *  3. „Woraus MAESTRO lernt" — die Referenzen-Fläche (Upload + Liste + Analyse-Status), von
 *     Katalog hierher umgezogen (catalog-references.tsx wird wiederverwendet; unter Katalog
 *     bleibt ein Verweis). REFERENZ_UPLOAD_ORT (apps/api/src/lib/betriebsreife.ts) nennt den
 *     neuen Ort — der Agent gibt Betreibern diesen Pfad weiter.
 *
 * Bausteine (knowledge_chunks) haben weiterhin zwei Rollen (Phase D v1):
 *  - Öffentlich: Quelle für den öffentlichen KI-Chat (RAG) — FAQ/Leistungen/Ablauf.
 *  - Intern: Betreiber-Gedächtnis (Ton du/Sie, Präferenzen, Kundennotizen) — fließt NUR in
 *    Angebots-Entwürfe & Anschreiben ein, NIE in den öffentlichen Chat.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import { Button, Card, EmptyState, ErrorNote, Icon, Input, LoadingRows, PageHeader, Select, SortierMenue, Zeichenzaehler, useReiterScroll, Checkbox, ButtonLink, Textarea } from "../ui/components";
import { useListSort, type SortSpalte } from "../hooks/useListSort";
import { Drawer } from "../ui/components";
import { useContentRefetch } from "../ui/CopilotProvider";
import { PlaybooksTab } from "./playbooks-tab";
import { TextbausteineTab } from "./textbausteine-tab";
import {
  EinsichtKarte,
  FaktKarte,
  HerkunftBadge,
  herkunftVonFakt,
  type Einsicht,
  type Fakt,
  type Herkunft,
  type Urteil,
} from "./beobachtet-tab";
import { ReferenceDocsSection } from "./catalog-references";
import { BedienerProfilKarte } from "../ui/BedienerProfilKarte";
import { GedaechtnisUeberblick } from "./gedaechtnis-ueberblick";
import { LernenTab } from "./lernen-tab";
import { KartenTab } from "./karten-tab";

// Drei Fragen statt drei Systembegriffe: „Bausteine/Skills/Beobachtet" setzte voraus, dass der
// Betreiber unsere Begriffe kennt. Die Reiter beantworten jetzt die Fragen, die er wirklich hat.
// Vierter Reiter (Punkt 9, Phase 2 Schritt 4): bewusst "textbausteine" statt "bausteine" als
// Key — LEGACY_TAB unten bildet den ALTEN Deep-Link-Key "bausteine" bereits auf "weiss" ab (er
// meinte frueher die knowledge_chunks-Baustein-Ansicht dort). Ein neuer Reiter mit demselben Key
// wuerde diese Abbildung stillschweigend ueberschreiben (TABS wird vor LEGACY_TAB geprueft) und
// alte "?tab=bausteine"-Links auf den falschen, leeren Reiter umlenken.
// Lern-Übersicht (Antoines Freigabe 25.09.2026): neuer ERSTER Reiter „Überblick“; die Seite heißt
// jetzt „Gedächtnis“ und liegt unter /gedaechtnis (/wissen leitet um, App.tsx).
const TABS = [
  { key: "ueberblick", label: "Überblick" },
  // Lernen S3 (27.09.2026): Schlüsse aus den Änderungen an KI-Entwürfen, direkt nach dem Überblick (Mockup).
  { key: "so-lerne-ich", label: "So lerne ich" },
  // Lernen S4: welche Karten und Pakete der Komponist für ein Datum sieht (Weg 1).
  { key: "karten", label: "Aus deinen Karten" },
  { key: "weiss", label: "Was MAESTRO weiß" },
  { key: "handelt", label: "Wie MAESTRO handelt" },
  { key: "lernt", label: "Woraus MAESTRO lernt" },
  { key: "textbausteine", label: "Textbausteine" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

// Alt-Keys aus Deep-Links im Bestand (z. B. NotificationsBell → "?tab=playbooks") bleiben
// funktionsfähig: sie werden still auf die neuen Reiter abgebildet, statt auf dem ersten Reiter
// zu stranden.
const LEGACY_TAB: Record<string, TabKey> = {
  bausteine: "weiss",
  beobachtet: "weiss",
  playbooks: "handelt",
  referenzen: "lernt",
};

type Visibility = "public" | "internal";

interface KnowledgeChunk {
  id: string;
  title: string;
  content: string;
  tags: string | null;
  active: boolean;
  source: "operator" | "agent";
  visibility: Visibility;
  customerId: string | null;
  updatedAt: string;
}

interface Customer {
  id: string;
  name: string;
}

const EMPTY = { title: "", content: "", tags: "", active: true, visibility: "public" as Visibility, customerId: "" };
type Form = typeof EMPTY;

// Design-Komponenten-Migration (Cluster Wissen-Bereich, 10.09.2026): Radix `Select.Item` erlaubt
// keinen leeren `value=""`-String, das Original hatte aber eine echte, jederzeit erneut waehlbare
// `<option value="">— Kein Kunde —</option>` — Sentinel-Wert nach dem etablierten Muster
// `MANDANTEN_STANDARD`/`KEIN_WERT` (s. `ui/NachschlagenTabs.tsx`), lokal in `""` zurueckuebersetzt.
const KEIN_KUNDE = "__kein_kunde__";

const badge = "rounded-full px-2 py-0.5 font-label-md text-label-md";

/**
 * Quick-Win (2026-07): Leerzustand OHNE jeden Eintrag. Vorher ein einzeiliger Hinweis ("Noch kein
 * Wissen hinterlegt"), der wie ein fehlendes Feature wirkte — es ist aber kein Bug, nur noch nichts
 * geschrieben. Erklärt jetzt zweigeteilt, WOHER Wissen kommt: was der Betreiber selbst anlegt, und
 * was ueber den Chat notiert wurde (source==='agent' = Antoine hat es dem Assistenten diktiert).
 * K1 (02.08.2026): Das Badge hiess "gelernt" - eine Behauptung, die nicht stimmte. source='agent'
 * heisst nur "ueber den Chat geschrieben", beobachtet hat das System nichts (Antoine sah seinen
 * eigenen diktierten Baustein als "gelernt" und schloss zu Recht: "das ganze scheint nicht zu
 * funktionieren"). Das Wort "gelernt" bleibt reserviert fuer BEOBACHTETE Fakten - die erscheinen
 * seit E4.8 mit Badge „Beobachtet" in derselben Liste, sobald es welche gibt.
 */
function KnowledgeEmptyState() {
  return (
    <EmptyState
      icon="menu_book"
      title="Noch kein Wissen hinterlegt"
      hint="Wissen speist sich aus zwei Quellen — was Sie selbst aufschreiben, und was der Assistent im Gespräch und aus Ihren Vorgängen bemerkt."
    >
      <div className="grid w-full max-w-2xl grid-cols-1 gap-4 text-left sm:grid-cols-2">
        <div className="rounded-2xl border border-outline-variant/40 bg-surface-container-low p-4 dark:border-outline/30 dark:bg-surface-variant/10">
          <div className="mb-2 flex items-center gap-2.5">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface-container-high text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
              <Icon name="person" className="text-[18px]" />
            </span>
            <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Von Ihnen</p>
          </div>
          <p className="font-body-sm text-[12.5px] leading-relaxed text-secondary dark:text-secondary-fixed-dim">
            Schreiben Sie oben einfach auf, was gelten soll — oder legen Sie über „Baustein hinzufügen" oben rechts
            gezielt FAQ-Antworten für den öffentlichen Chat und interne Notizen zu Ton und Ablauf an.
          </p>
        </div>
        <div className="rounded-2xl border border-outline-variant/40 bg-surface-container-low p-4 dark:border-outline/30 dark:bg-surface-variant/10">
          <div className="mb-2 flex items-center gap-2.5">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary-container text-primary">
              <Icon name="auto_awesome" className="text-[18px]" />
            </span>
            <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Beobachtet</p>
          </div>
          <p className="font-body-sm text-[12.5px] leading-relaxed text-secondary dark:text-secondary-fixed-dim">
            MAESTRO hält fest, was es aus Ihren Angeboten, Entscheidungen und Gesprächen über den Betrieb gelernt hat —
            mit Beleg. Sobald etwas entsteht, erscheint es hier, und Sie können widersprechen.
          </p>
        </div>
      </div>
    </EmptyState>
  );
}

/**
 * EIN Eingabefeld „Wissen hinzufügen": Freitext → POST /api/gedaechtnis/fakt. Der Server setzt
 * quelle='mensch', status='confirmed', Konfidenz 1.0 — der Nutzer entscheidet NIE einen Typ
 * (weder Baustein vs. Fakt noch Sichtbarkeit). Wer die feineren Baustein-Optionen braucht
 * (öffentlich/intern, Kunde, Tags), nimmt „Baustein hinzufügen" oben rechts — das bleibt der
 * „Erweitert"-Weg.
 */
function WissenHinzufuegen({ onSaved }: { onSaved: () => void }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const aussage = text.trim();
    if (aussage.length < 3) return;
    setBusy(true);
    setError(null);
    try {
      await apiMutate("/api/gedaechtnis/fakt", "POST", { aussage });
      setText("");
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="form" className="mb-4 p-4" onSubmit={(e) => void submit(e)}>
      <div className="flex flex-wrap items-center gap-2.5">
        <label className="relative min-w-[240px] flex-1">
          <Icon name="lightbulb" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant" />
          <Input
            className="pl-9"
            placeholder="Wissen hinzufügen — z. B. „Wir liefern nur bis 30 km“ oder „Wir duzen unsere Gäste“"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={500}
          />
          <Zeichenzaehler current={text.length} max={500} />
        </label>
        <Button type="submit" className="shrink-0" disabled={busy || text.trim().length < 3}>
          <Icon name="add" className="text-[18px]" /> {busy ? "Speichert …" : "Hinzufügen"}
        </Button>
      </div>
      <p className="mt-1.5 font-label-md text-label-md text-on-surface-variant">
        Einfach als Satz aufschreiben — MAESTRO speichert es als bestätigtes Wissen und berücksichtigt es künftig von selbst.
      </p>
      {error && <p className="mt-1.5 font-body-sm text-body-sm text-error">{error}</p>}
    </Card>
  );
}

/** Ein Eintrag der verschmolzenen Liste — Baustein oder Fakt, sortierbar über ein Datum. */
type WissensEintrag =
  | { art: "baustein"; datum: number; chunk: KnowledgeChunk }
  | { art: "fakt"; datum: number; fakt: Fakt };

/** Listenstandard (Brief § 9, Punkt 95): Kartenliste ohne Spaltenköpfe — Sortierung als Menü.
 *  Standard = „Zuletzt geändert“ absteigend (so entsteht `eintraege` ohnehin). „Typ“ folgt der
 *  Reihenfolge des Herkunfts-Umschalters: Von Ihnen < Beobachtet < Aus Referenzen. */
const WISSEN_SPALTEN: SortSpalte<WissensEintrag>[] = [
  { key: "titel", label: "Titel", type: "text", accessor: (e) => (e.art === "baustein" ? e.chunk.title : e.fakt.aussage) },
  {
    key: "typ",
    label: "Typ",
    type: "status",
    accessor: (e) => (e.art === "baustein" ? "ihnen" : herkunftVonFakt(e.fakt)),
    statusOrder: { ihnen: 0, beobachtet: 1, referenzen: 2 },
  },
];
const WISSEN_SORTIERUNG = [
  { label: "Zuletzt geändert", key: null },
  { label: "Titel A–Z", key: "titel", dir: "asc" },
  { label: "Typ", key: "typ", dir: "asc" },
] as const;

export default function KnowledgePage() {
  const { data, loading, error, reload } = useApi<KnowledgeChunk[]>("/api/knowledge");
  const { data: customersData } = useApi<Customer[]>("/api/customers");
  // E4.8 / Wissen-Umbau: Beobachtetes (Fakten + Einsichten) lebt in derselben Fläche wie die
  // Bausteine — deshalb lädt die Seite beide Quellen und verschmilzt sie unten zu EINER Liste.
  const beobachtetQ = useApi<{ fakten: Fakt[]; einsichten: Einsicht[] }>("/api/gedaechtnis/beobachtet");

  // Quick-Win (2026-07): der Agent schreibt im Chat diktierte Praeferenzen ueber knowledge_write und meldet
  // changed:{resource:"knowledge"} (apps/agent/src/maestro-agent.ts) - eine offene /wissen-Seite
  // hat das bisher NICHT abonniert und blieb bis zum manuellen Neuladen veraltet.
  useContentRefetch("knowledge", () => {
    reload();
    beobachtetQ.reload();
  });

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Urteil (Stimmt / Stimmt nicht / Ausblenden) auf Fakten und Einsichten — exakt die Mechanik
  // des früheren Beobachtet-Reiters (beobachtet-tab.tsx).
  const [urteilBusy, setUrteilBusy] = useState<string | null>(null);
  const [urteilFehler, setUrteilFehler] = useState<string | null>(null);

  const chunks = data ?? [];
  const fakten = beobachtetQ.data?.fakten ?? [];
  const einsichten = beobachtetQ.data?.einsichten ?? [];
  const customers = useMemo(() => customersData ?? [], [customersData]);
  const customerName = useMemo(() => new Map(customers.map((c) => [c.id, c.name])), [customers]);

  // A-06: Hilfe-Panel und Topbar-Suche navigieren mit ?q=... hierher. Diese Seite wertet den
  // Parameter aus (initial + reaktiv) und filtert die Wissens-Einträge nach Text.
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("q") ?? "");
  useEffect(() => {
    const q = params.get("q");
    if (q != null) setSearch(q);
  }, [params]);

  // S1.4: Tab in der URL (?tab=...), Muster wie settings.tsx - deep-linkbar, uebersteht Reload.
  // Alt-Keys (bausteine/playbooks/beobachtet) werden auf die neuen Reiter abgebildet.
  const rawTab = params.get("tab");
  // Ohne Reiter: Überblick. Eine Suche (?q=, Hilfe-Panel/Topbar) oder Sortierung (?sort=) meint
  // aber die Liste „Was MAESTRO weiß“ - so bleiben alte Such-Links gültig.
  const ohneReiter: TabKey = params.get("q") != null || params.get("sort") != null ? "weiss" : "ueberblick";
  const tab: TabKey = TABS.some((t) => t.key === rawTab)
    ? (rawTab as TabKey)
    : (rawTab != null && LEGACY_TAB[rawTab]) || ohneReiter;
  const setTab = (key: TabKey) => setParams(key === "ueberblick" ? {} : { tab: key }, { replace: true });
  const reiter = useReiterScroll<HTMLDivElement>(tab);
  const [neuerHinweis, setNeuerHinweis] = useState(false);

  // Herkunfts-Filter „Alle | Von Ihnen | Beobachtet | Aus Referenzen" — löst den früheren
  // Baustein-Filter (Manuell / Im Chat notiert) ab: die Frage ist jetzt WOHER das Wissen stammt,
  // nicht über welchen Eingabeweg es kam. Rein clientseitig (beide Listen sind komplett geladen).
  const [herkunftFilter, setHerkunftFilter] = useState<"alle" | Herkunft>("alle");

  // Verschmolzene Liste: Bausteine + Fakten, neueste zuerst (Baustein: updatedAt, Fakt:
  // createdAt). Einsichten stehen als eigener Block DARÜBER: sie sind wenige, tragen den
  // stärksten Widerspruchs-Wert (Muster mit Belegen) und sollen nicht zwischen fünfzig
  // Einzel-Fakten untergehen — pragmatische Sortier-Entscheidung dieses Umbaus.
  const eintraege = useMemo<WissensEintrag[]>(() => {
    const list: WissensEintrag[] = [
      ...chunks.map((chunk): WissensEintrag => ({ art: "baustein", datum: Date.parse(chunk.updatedAt) || 0, chunk })),
      ...fakten.map((fakt): WissensEintrag => ({ art: "fakt", datum: Date.parse(fakt.createdAt) || 0, fakt })),
    ];
    return list.sort((a, b) => b.datum - a.datum);
  }, [chunks, fakten]);

  // Bausteine sind immer „Von Ihnen" — auch source==='agent' heißt nur „über den Chat
  // diktiert", nicht „beobachtet" (K1, 02.08.2026). Beobachtetes kommt als Fakt/Einsicht.
  const herkunftVon = (e: WissensEintrag): Herkunft => (e.art === "baustein" ? "ihnen" : herkunftVonFakt(e.fakt));

  const herkunftCounts = useMemo(() => {
    const counts = { alle: eintraege.length + einsichten.length, ihnen: 0, beobachtet: einsichten.length, referenzen: 0 };
    for (const e of eintraege) counts[herkunftVon(e)] += 1;
    return counts;
  }, [eintraege, einsichten]);

  const passtZurSuche = (e: WissensEintrag, q: string): boolean =>
    e.art === "baustein"
      ? e.chunk.title.toLowerCase().includes(q) || e.chunk.content.toLowerCase().includes(q) || (e.chunk.tags ?? "").toLowerCase().includes(q)
      : e.fakt.aussage.toLowerCase().includes(q);

  const sort = useListSort("wissen", WISSEN_SPALTEN);
  const gefiltert = sort.sortiere(
    useMemo(() => {
      const q = search.trim().toLowerCase();
      return eintraege
        .filter((e) => herkunftFilter === "alle" || herkunftVon(e) === herkunftFilter)
        .filter((e) => !q || passtZurSuche(e, q));
    }, [eintraege, herkunftFilter, search]),
  );

  const gefilterteEinsichten = useMemo(() => {
    if (herkunftFilter !== "alle" && herkunftFilter !== "beobachtet") return [];
    const q = search.trim().toLowerCase();
    return einsichten.filter((e) => !q || e.titel.toLowerCase().includes(q) || e.begruendung.toLowerCase().includes(q));
  }, [einsichten, herkunftFilter, search]);

  function openNew() {
    setEditId(null);
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  }
  function openEdit(k: KnowledgeChunk) {
    setEditId(k.id);
    setForm({ title: k.title, content: k.content, tags: k.tags ?? "", active: k.active, visibility: k.visibility, customerId: k.customerId ?? "" });
    setFormError(null);
    setOpen(true);
  }

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const body = {
        title: form.title,
        content: form.content,
        tags: form.tags || null,
        active: form.active,
        visibility: form.visibility,
        customerId: form.customerId || null,
      };
      if (editId) await apiMutate(`/api/knowledge/${editId}`, "PATCH", body);
      else await apiMutate("/api/knowledge", "POST", body);
      setOpen(false);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate(`/api/knowledge/${id}`, "DELETE");
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  // Stimmt / Stimmt nicht / Ausblenden — derselbe Weg wie im früheren Beobachtet-Reiter:
  // `stimmt_nicht` setzt auf disputed, die Konsolidierung (sql/192) frischt es nicht mehr auf.
  async function urteilen(art: "fakt" | "einsicht", id: string, urteil: Urteil) {
    setUrteilBusy(id);
    setUrteilFehler(null);
    try {
      await apiMutate(`/api/gedaechtnis/${art}/${id}/status`, "POST", { urteil });
      beobachtetQ.reload();
    } catch (e) {
      setUrteilFehler(e instanceof Error ? e.message : "Das Urteil konnte nicht gespeichert werden.");
    } finally {
      setUrteilBusy(null);
    }
  }

  /**
   * „Bearbeiten" für vom Betreiber selbst geschriebene Fakten (quelle==='mensch') — KEIN echtes
   * UPDATE (sql/185 verbietet UPDATE/DELETE auf tenant_facts absichtlich, Memory-Poisoning-Schutz).
   * Stattdessen: erst der korrigierte Text als neuer bestätigter Fakt anlegen, DANACH den alten
   * über den bestehenden Status-Weg ausblenden (`retired`) — in dieser Reihenfolge, damit ein
   * Netzwerkfehler höchstens einen doppelten (statt gar keinen) Eintrag hinterlässt; ein Duplikat
   * lässt sich über den normalen „Ausblenden"-Knopf beheben, ein verlorener Text nicht.
   */
  async function faktBearbeiten(fakt: Fakt, neuerText: string) {
    setUrteilBusy(fakt.id);
    setUrteilFehler(null);
    try {
      await apiMutate("/api/gedaechtnis/fakt", "POST", { aussage: neuerText, anlass: "korrektur" });
      await apiMutate(`/api/gedaechtnis/fakt/${fakt.id}/status`, "POST", { urteil: "ausblenden" });
      beobachtetQ.reload();
    } catch (e) {
      setUrteilFehler(e instanceof Error ? e.message : "Die Korrektur konnte nicht gespeichert werden.");
    } finally {
      setUrteilBusy(null);
    }
  }

  const allesLeer = chunks.length === 0 && fakten.length === 0 && einsichten.length === 0;
  const laedt = loading || beobachtetQ.loading;

  return (
    <>
      <PageHeader
        title="Gedächtnis"
        subtitle={
          tab === "ueberblick"
            ? "Alles, was der Assistent über Ihren Betrieb gelernt hat, woher er es hat und wo er es benutzt. Jeden Eintrag können Sie bestätigen, korrigieren, abschalten oder löschen."
            : tab === "so-lerne-ich"
            ? "Was MAESTRO aus deinen Änderungen an KI-Entwürfen schließt — mit Belegen. Du entscheidest, was gilt."
            : tab === "karten"
            ? "Welche Karten und Pakete der Komponist für ein Datum nutzt — und welche nicht, mit Grund."
            : tab === "handelt"
            ? "Kurze Anweisungen, wie der Assistent in bestimmten Lagen handelt — Sie legen sie an, nur Inhaber aktivieren sie."
            : tab === "lernt"
              ? "Laden Sie bisherige Angebote hoch — MAESTRO lernt daraus Aufbau, Schreibweise und Zusatzleistungen Ihres Betriebs."
              : tab === "textbausteine"
                ? "Wiederverwendbare Text-Schnipsel für die Mail-Komposition — im Angebots-Builder, bei Kostenübernahme, Rechnung und Erinnerungen abrufbar."
                : "Alles, was MAESTRO über Ihren Betrieb weiß — von Ihnen gesagt, beobachtet oder aus Ihren Referenzen. Wenn etwas nicht stimmt: widersprechen."
        }
        actions={
          tab === "so-lerne-ich" ? (
            <Button type="button" onClick={() => setNeuerHinweis(true)}>
              <Icon name="add" className="text-[18px]" /> Eigener Hinweis
            </Button>
          ) : tab === "weiss" ? (
            <>
              {/* CX-Audit 2026-07-17: Die Seite pflegt den KI-Chat, bot aber keinen Weg, das
                  Ergebnis zu SEHEN — „Chat testen" öffnet die öffentliche Chat-Seite im neuen Tab. */}
              <ButtonLink href="/chat" target="_blank" rel="noreferrer" variant="secondary">
                <Icon name="open_in_new" className="text-[18px]" /> Chat testen
              </ButtonLink>
              {/* „Erweitert": der Baustein-Editor (Titel, öffentlich/intern, Kunde, Tags) bleibt
                  vollständig erhalten — für alle, die mehr steuern wollen als der Freitext oben. */}
              <Button type="button" onClick={openNew}>
                <Icon name="add" className="text-[18px]" /> Baustein hinzufügen
              </Button>
            </>
          ) : undefined
        }
      />

      {/* S1.4: Reiter-Leiste, Muster wie settings.tsx (border-b, space-x-8). */}
      <div ref={reiter.ref} data-rand={reiter.rand || undefined} className="mb-6 tab-scroll flex gap-8 border-b border-outline-variant/30 dark:border-outline/30">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-state={tab === t.key ? "active" : undefined}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px whitespace-nowrap border-b-2 pb-4 font-label-md text-[12.5px] font-semibold transition-colors ${
              tab === t.key
                ? "border-primary text-primary dark:border-primary-fixed dark:text-primary-fixed"
                : "border-transparent text-on-surface-variant hover:text-primary dark:text-secondary-fixed-dim dark:hover:text-primary-fixed"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "ueberblick" && <GedaechtnisUeberblick />}
      {tab === "so-lerne-ich" && <LernenTab neuerHinweis={neuerHinweis} onHinweisZu={() => setNeuerHinweis(false)} />}
      {tab === "karten" && <KartenTab />}
      {tab === "handelt" && <PlaybooksTab />}
      {/* Woraus MAESTRO lernt: die komplette Referenzen-Fläche (Upload, Liste, Analyse-Status,
          Stilprofil) — wiederverwendet aus catalog-references.tsx, umgezogen von /katalog. */}
      {tab === "lernt" && <ReferenceDocsSection />}
      {/* Textbausteine (Punkt 9): betriebsweiter Pool fuer die Mail-Komposition, eigene Tabelle
          text_snippets — s. docs/KONZEPT-VERSANDMODUS-UND-TEXTBAUSTEINE.md § 2.1. */}
      {tab === "textbausteine" && <TextbausteineTab />}

      {tab === "weiss" && (
      <>
      {beobachtetQ.error && !error && <ErrorNote message={beobachtetQ.error} onRetry={beobachtetQ.reload} />}
      {formError && <ErrorNote message={formError} />}
      {urteilFehler && <ErrorNote message={urteilFehler} />}

      <WissenHinzufuegen onSaved={() => beobachtetQ.reload()} />

      {/* S4.1: Das Bediener-Profil gehört hierher und nirgendwo sonst — es ist der Teil dessen,
          was MAESTRO weiß, der über EINEN MENSCHEN geht. Bewusst nicht in der verschmolzenen
          Liste darunter: die zeigt Wissen über den BETRIEB (und ist für alle Kolleginnen und
          Kollegen dieselbe), diese Karte sieht nur der Betroffene selbst. */}
      <BedienerProfilKarte />

      {!allesLeer && (
        <div className="mb-3 flex flex-wrap items-center gap-2.5">
          <label className="relative min-w-[220px] flex-1">
            <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant" />
            <Input
              className="pl-9"
              placeholder="Wissen durchsuchen …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          {/* Herkunfts-Umschalter — Segmented-Control im Kalender-Stil (Monat/Woche-Toggle),
              damit die drei Herkünfte auf einen Blick unterscheidbar und filterbar sind. */}
          <div className="flex shrink-0 items-center gap-0.5 rounded-[10px] border border-outline-variant/60 bg-surface-container-lowest p-[3px] dark:border-outline/40 dark:bg-inverse-surface">
            {(
              [
                { key: "alle", label: "Alle" },
                { key: "ihnen", label: "Von Ihnen" },
                { key: "beobachtet", label: "Beobachtet" },
                { key: "referenzen", label: "Aus Referenzen" },
              ] as const
            ).map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setHerkunftFilter(f.key)}
                className={`rounded-lg px-3.5 py-1.5 font-label-md text-[12px] font-semibold ${
                  herkunftFilter === f.key ? "bg-primary text-on-primary" : "text-secondary-fixed-dim"
                }`}
              >
                {f.label} <span className="opacity-70">{herkunftCounts[f.key]}</span>
              </button>
            ))}
          </div>
          <SortierMenue optionen={WISSEN_SORTIERUNG} sortKey={sort.sortKey} dir={sort.dir} onWahl={sort.setze} />
        </div>
      )}

      {laedt ? (
        <Card className="overflow-hidden"><LoadingRows rows={3} /></Card>
      ) : error ? (
        <Card className="overflow-hidden">
          <ErrorNote
            zentriert
            title="Wissen konnte nicht geladen werden"
            message={error}
            onRetry={() => {
              reload();
              beobachtetQ.reload();
            }}
          />
        </Card>
      ) : allesLeer ? (
        <Card className="overflow-hidden"><KnowledgeEmptyState /></Card>
      ) : gefiltert.length === 0 && gefilterteEinsichten.length === 0 ? (
        <Card className="overflow-hidden">
          {search.trim() ? (
            <EmptyState
              icon="search_off"
              title="Kein Eintrag passt zur Suche"
              hint={`Keine Treffer für „${search.trim()}".`}
              link={{
                label: "Alle Einträge anzeigen",
                onClick: () => {
                  setSearch("");
                  setHerkunftFilter("alle");
                },
              }}
            />
          ) : herkunftFilter === "beobachtet" ? (
            <EmptyState
              icon="visibility"
              title="Noch nichts beobachtet"
              hint="MAESTRO hält hier fest, was es aus Ihren Angeboten, Entscheidungen und Gesprächen über den Betrieb gelernt hat — mit Beleg. Sobald etwas entsteht, können Sie hier widersprechen."
            />
          ) : herkunftFilter === "referenzen" ? (
            <EmptyState
              icon="menu_book"
              title="Noch nichts aus Referenzen übernommen"
              hint='Unter „Woraus MAESTRO lernt" können Sie bisherige Angebote hochladen — daraus übernommenes Wissen erscheint dann hier.'
            />
          ) : (
            <EmptyState
              icon="person"
              title="Noch nichts von Ihnen hinterlegt"
              hint='Schreiben Sie oben einen Satz ins Eingabefeld — oder legen Sie über „Baustein hinzufügen" oben rechts einen Baustein an (z. B. „Anfahrt & Liefergebiet" öffentlich oder „Tonalität: wir duzen" intern).'
            />
          )}
        </Card>
      ) : (
        <div className="flex flex-col gap-8">
          {/* Muster (Einsichten) als eigener Block ÜBER den Einzel-Einträgen: wenige, belegt,
              mit dem stärksten Widerspruchs-Wert — Code unverändert aus beobachtet-tab.tsx. */}
          {gefilterteEinsichten.length > 0 && (
            <section>
              <h2 className="mb-1 font-serif text-[19px] font-semibold italic text-on-background dark:text-inverse-on-surface">Muster, die mir aufgefallen sind</h2>
              <p className="mb-3 text-[12.5px] text-on-surface-variant dark:text-surface-variant">
                Jede Zeile stützt sich auf gezählte Vorgänge — nicht auf einen Eindruck.
              </p>
              <div className="flex flex-col gap-3">
                {gefilterteEinsichten.map((e) => (
                  <EinsichtKarte key={e.id} einsicht={e} busy={urteilBusy === e.id} onUrteil={(u) => void urteilen("einsicht", e.id, u)} />
                ))}
              </div>
            </section>
          )}

          {gefiltert.length > 0 && (
            <section>
              <h2 className="mb-1 font-serif text-[19px] font-semibold italic text-on-background dark:text-inverse-on-surface">Was ich über den Betrieb weiß</h2>
              <p className="mb-3 text-[12.5px] text-on-surface-variant dark:text-surface-variant">
                Von Ihnen Aufgeschriebenes lässt sich bearbeiten — Beobachtetem lässt sich widersprechen. Es kommt dann nicht wieder.
              </p>
              <div className="flex flex-col gap-3">
                {gefiltert.map((e) =>
                  e.art === "fakt" ? (
                    <FaktKarte
                      key={e.fakt.id}
                      fakt={e.fakt}
                      busy={urteilBusy === e.fakt.id}
                      onUrteil={(u) => void urteilen("fakt", e.fakt.id, u)}
                      onBearbeiten={(text) => void faktBearbeiten(e.fakt, text)}
                    />
                  ) : (
                    // Baustein-Karte: die komplette bisherige Zeilen-Funktionalität (Badges,
                    // Bearbeiten, Löschen) — nur als Karte im Stil der Fakt-Karten daneben.
                    <div key={e.chunk.id} className="rounded-2xl border border-outline-variant bg-surface-container-lowest px-4 py-3.5 dark:border-white/10 dark:bg-white/[0.04]">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{e.chunk.title}</p>
                            <HerkunftBadge herkunft="ihnen" />
                            {e.chunk.visibility === "internal" ? (
                              <span className={`${badge} bg-tertiary-container text-on-tertiary-container`}>intern</span>
                            ) : (
                              <span className={`${badge} bg-secondary-container text-on-secondary-container`}>öffentlich</span>
                            )}
                            {e.chunk.source === "agent" && <span className={`${badge} bg-primary-container text-on-primary-container`}>im Chat notiert</span>}
                            {e.chunk.customerId && (
                              <span className={`${badge} bg-surface-container-high text-secondary dark:bg-white/[0.08]`}>
                                {customerName.get(e.chunk.customerId) ?? "Kunde"}
                              </span>
                            )}
                            {!e.chunk.active && <span className={`${badge} bg-surface-container-high text-secondary dark:bg-white/[0.08]`}>inaktiv</span>}
                          </div>
                          <p className="mt-1 line-clamp-2 font-body-sm text-body-sm text-on-surface-variant">{e.chunk.content}</p>
                          {e.chunk.tags && <p className="mt-1 font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">{e.chunk.tags}</p>}
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          <button type="button" className="inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline" onClick={() => openEdit(e.chunk)}>
                            <Icon name="edit" className="text-[16px]" /> Bearbeiten
                          </button>
                          <button type="button" className="inline-flex items-center gap-1 font-label-md text-label-md text-error hover:underline disabled:opacity-50" disabled={busy} onClick={() => void remove(e.chunk.id)}>
                            <Icon name="delete" className="text-[16px]" /> Löschen
                          </button>
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </section>
          )}
        </div>
      )}

      <Drawer open={open} onClose={() => setOpen(false)} title={editId ? "Baustein bearbeiten" : "Baustein hinzufügen"}>
        <form className="space-y-4" onSubmit={(e) => void save(e)}>
          {formError && <ErrorNote message={formError} />}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Titel *</span>
            <Input required placeholder="z. B. Anfahrt & Liefergebiet" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Inhalt *</span>
            <Textarea required rows={8} placeholder="Klartext — z. B. FAQ-Antwort (öffentlich) oder Tonalität wie ‚wir duzen unsere Gäste' (intern) …" value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Sichtbarkeit</span>
            <Select
              ariaLabel="Sichtbarkeit"
              value={form.visibility}
              onValueChange={(v) => setForm((f) => ({ ...f, visibility: v as Visibility }))}
              options={[
                { value: "public", label: "Öffentlich – erscheint im KI-Chat" },
                { value: "internal", label: "Intern – nur für Angebote & Anschreiben" },
              ]}
            />
            <span className="mt-1 block font-label-md text-label-md text-on-surface-variant">
              Interne Bausteine (Ton, Präferenzen, Kundennotizen) bleiben aus dem öffentlichen Chat heraus.
            </span>
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Kunde (optional)</span>
            <Select
              ariaLabel="Kunde (optional)"
              value={form.customerId || KEIN_KUNDE}
              onValueChange={(v) => setForm((f) => ({ ...f, customerId: v === KEIN_KUNDE ? "" : v }))}
              options={[
                { value: KEIN_KUNDE, label: "— Kein Kunde —" },
                ...customers.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
            <span className="mt-1 block font-label-md text-label-md text-on-surface-variant">Für Kundennotizen — meist zusammen mit „Intern".</span>
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Tags</span>
            <Input placeholder="anfahrt, lieferung, preise" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} />
          </label>
          <label className="flex items-center gap-2 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
            <Checkbox checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} /> Aktiv (wird verwendet)
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button type="submit" disabled={busy}>{busy ? "Speichert …" : "Speichern"}</Button>
          </div>
        </form>
      </Drawer>
      </>
      )}
    </>
  );
}
